// react
import React, { useState, useEffect, useMemo } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TableFooter, TablePagination, useTheme, IconButton } from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon, ArrowForwardIos as ArrowForwardIosIcon } from "@mui/icons-material";

// utils
import formatDate from "utils/formatDate";
import { getStatusChip } from "./utils/styleGetters";

// A ---------------------------------------------------------------------- M

const FlowEventsTableHead = () => {
  const { t } = useTranslation();
  const headCells = useMemo(
    () => [
      { id: "sourceName", label: t("name") },
      { id: "timestamp", label: t("timestamp") },
      { id: "transition", label: t("transition") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ backgroundColor: "grey.100" }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="left">
            <Typography variant="body2" fontWeight="bold">
              {headCell.label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  const handleBack = (e) => onPageChange(e, page - 1);
  const handleNext = (e) => onPageChange(e, page + 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleBack} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={handleNext} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

const FlowEventsTable = ({ flowEventsData }) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filteredEvents, setFilteredEvents] = useState([]);

  useEffect(() => {
    setFilteredEvents(flowEventsData.filter((event) => event.previousState && event.state));
  }, [flowEventsData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredEvents.length) : 0;

  return (
    <TableContainer>
      <Table>
        <FlowEventsTableHead />
        <TableBody>
          {(rowsPerPage > 0 ? filteredEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : filteredEvents).map((event) => (
            <TableRow key={event.id} hover>
              <TableCell align="left">{event.sourceName}</TableCell>
              <TableCell align="left">{event.timestamp ? formatDate(event.timestamp) : "N/A"}</TableCell>
              <TableCell align="left">
                <Box display="flex" alignItems="center">
                  {getStatusChip(event.previousState)}
                  <ArrowForwardIosIcon sx={{ fontSize: "1rem", mx: 1 }} />
                  {getStatusChip(event.state)}
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={3} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter sx={{ backgroundColor: "grey.100" }}>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 20]}
              colSpan={3}
              count={filteredEvents.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t("rows_per_page")}
              labelDisplayedRows={({ from, to, count }) =>
                t("displayed_rows", {
                  from,
                  to,
                  count: count !== -1 ? count : `more than ${to}`,
                })
              }
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default FlowEventsTable;
